import { useEffect, useState, Fragment } from "react";
import { api, API, eur, RATE_CODES, RATE_LABELS } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Printer, Download, FileBarChart, BookText, FileText, FileSpreadsheet } from "lucide-react";

const T = {
  nl: { title: "BTW-periode rapport", rate: "Tarief", gross: "Bruto ontvangsten (incl.)", base: "Maatstaf (excl.)", vat: "BTW", total: "Totaal", central: "Centralisatie per vestiging", corr: "Waarvan correcties", entries: "boekingen", print: "Afdrukken", export: "Exporteren (CSV)", lang: "Taal / Language" },
  en: { title: "VAT period report", rate: "Rate", gross: "Gross receipts (incl.)", base: "Taxable base (excl.)", vat: "VAT", total: "Total", central: "Centralisation per location", corr: "Of which corrections", entries: "entries", print: "Print", export: "Export (CSV)", lang: "Taal / Language" },
};

export default function Reports() {
  const [lang, setLang] = useState("nl");
  const t = T[lang];
  const today = new Date();
  const [start, setStart] = useState(new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10));
  const [end, setEnd] = useState(today.toISOString().slice(0, 10));
  const [locations, setLocations] = useState([]);
  const [locId, setLocId] = useState("all");
  const [report, setReport] = useState(null);
  const [saldo, setSaldo] = useState(null);
  const [aangifte, setAangifte] = useState(null);
  const [kbBusy, setKbBusy] = useState("");

  useEffect(() => { api.get("/locations").then((r) => setLocations(r.data)); }, []);
  const run = () => api.get("/reports/period", { params: { start, end, location_id: locId } }).then((r) => setReport(r.data));
  useEffect(() => { run(); }, [start, end, locId]);
  useEffect(() => {
    api.get("/factuurboek/btw-saldo", { params: { from_date: start, to_date: end } })
      .then((r) => setSaldo(r.data)).catch(() => setSaldo(null));
    api.get("/factuurboek/btw-aangifte", { params: { from_date: start, to_date: end } })
      .then((r) => setAangifte(r.data.roosters)).catch(() => setAangifte(null));
  }, [start, end]);

  const downloadKostenboek = async (fmt) => {
    setKbBusy(fmt);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/factuurboek/export/kostenboek?from_date=${start}&to_date=${end}&format=${fmt}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("mislukt");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `BTW-boek_kosten_${start}_${end}.${fmt === "xlsx" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`BTW-boek geëxporteerd (${fmt.toUpperCase()})`);
    } catch (e) { toast.error("Kon BTW-boek niet exporteren"); }
    finally { setKbBusy(""); }
  };

  const downloadAangifte = async (fmt) => {
    setKbBusy(`aangifte-${fmt}`);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/factuurboek/btw-aangifte/export?from_date=${start}&to_date=${end}&format=${fmt}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("mislukt");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `BTW-aangifte_${start}_${end}.${fmt === "xlsx" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`BTW-aangifte gedownload (${fmt.toUpperCase()})`);
    } catch (e) { toast.error("Kon BTW-aangifte niet downloaden"); }
    finally { setKbBusy(""); }
  };

  const downloadGrootboek = async (fmt) => {
    setKbBusy(`gb-${fmt}`);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/factuurboek/export/grootboek?from_date=${start}&to_date=${end}&format=${fmt}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("mislukt");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Grootboek_aankopen_${start}_${end}.${fmt === "xlsx" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Grootboek geëxporteerd (${fmt.toUpperCase()})`);
    } catch (e) { toast.error("Kon grootboek niet exporteren"); }
    finally { setKbBusy(""); }
  };

  const exportCsv = () => {
    if (!report) return;
    let csv = `${t.title};${start};${end}\n${t.rate};${t.gross};${t.base};${t.vat}\n`;
    RATE_CODES.forEach((r) => {
      const d = report.per_rate[r];
      csv += `${RATE_LABELS[r]};${d.gross};${d.base};${d.vat}\n`;
    });
    csv += `${t.total};${report.total.gross};${report.total.base};${report.total.vat}\n`;
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `btw-rapport-${start}_${end}.csv`;
    a.click();
  };

  const chartData = report ? RATE_CODES.map((r) => ({ name: RATE_LABELS[r], base: report.per_rate[r].base, vat: report.per_rate[r].vat })) : [];

  const [downloading, setDownloading] = useState(false);
  const downloadBook = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/reports/dagontvangstenboek?location_id=${locId}&start=${start}&end=${end}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("mislukt");
      const blob = await res.blob();
      const base = locId === "all" ? "Centralisatieboek" : (locations.find((l) => l.id === locId)?.name || "vestiging").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Dagontvangsten_${base}_${start}_${end}.pdf`;
      a.click();
      let verschil = {};
      try { verschil = JSON.parse(res.headers.get("X-Aansluiting-Verschil") || "{}"); } catch { verschil = {}; }
      const fouten = Object.entries(verschil);
      if (fouten.length) {
        toast.error(`Let op: detaillijnen en BTW-overzicht verschillen (${fouten.map(([c, v]) => `${RATE_LABELS[c] || c}: ${eur(v)}`).join("; ")})`, { duration: 15000 });
      } else {
        toast.success((locId === "all" ? "Centralisatieboek geëxporteerd" : "Dagontvangstenboek geëxporteerd") + " · lijnen = BTW-overzicht ✓");
      }
    } catch (e) {
      toast.error("Kon boek niet exporteren");
    } finally {
      setDownloading(false);
    }
  };

  const [overzExporting, setOverzExporting] = useState(false);
  const exportOverzicht = async () => {
    if (locId === "all") return toast.error("Kies een specifieke vestiging voor de Z-Tickets + Overzicht export");
    setOverzExporting(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/export/ztickets-overzicht?location_id=${locId}&start=${start}&end=${end}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("mislukt");
      const blob = await res.blob();
      const base = (locations.find((l) => l.id === locId)?.name || "vestiging").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Z-Tickets_Overzicht_${base}_${start}_${end}.pdf`;
      a.click();
      toast.success("Z-Tickets + Overzicht geëxporteerd");
    } catch (e) {
      toast.error("Kon Z-Tickets + Overzicht niet exporteren");
    } finally {
      setOverzExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-end justify-between flex-wrap gap-4 no-print">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-2">
            <FileBarChart className="h-7 w-7 text-primary" /> {t.title}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Periodetotalen per BTW-tarief · Singh Express Shop BV · BE 0788.952.072</p>
        </div>
        <div className="flex gap-2">
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger className="w-28" data-testid="report-lang"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="nl">Nederlands</SelectItem><SelectItem value="en">English</SelectItem></SelectContent>
          </Select>
          <Button onClick={downloadBook} disabled={downloading} data-testid="download-book">
            <BookText className="h-4 w-4 mr-2" />{locId === "all" ? "Centralisatieboek (PDF)" : "Dagontvangstenboek (PDF)"}
          </Button>
          <Button variant="outline" onClick={exportOverzicht} disabled={overzExporting} data-testid="download-overzicht">
            <FileText className="h-4 w-4 mr-2" />{overzExporting ? "Bezig…" : "Z-Tickets + Overzicht (PDF)"}
          </Button>
          <Button variant="outline" onClick={() => downloadKostenboek("pdf")} disabled={kbBusy === "pdf"} data-testid="download-kostenboek-pdf">
            <FileText className="h-4 w-4 mr-2" />{kbBusy === "pdf" ? "Bezig…" : "BTW-boek kosten (PDF)"}
          </Button>
          <Button variant="outline" onClick={() => downloadKostenboek("xlsx")} disabled={kbBusy === "xlsx"} data-testid="download-kostenboek-xlsx">
            <FileSpreadsheet className="h-4 w-4 mr-2" />{kbBusy === "xlsx" ? "Bezig…" : "BTW-boek (Excel)"}
          </Button>
          <Button variant="outline" onClick={() => downloadGrootboek("pdf")} disabled={kbBusy === "gb-pdf"} data-testid="download-grootboek-pdf">
            <FileText className="h-4 w-4 mr-2" />{kbBusy === "gb-pdf" ? "Bezig…" : "Grootboek (PDF)"}
          </Button>
          <Button variant="outline" onClick={() => downloadGrootboek("xlsx")} disabled={kbBusy === "gb-xlsx"} data-testid="download-grootboek-xlsx">
            <FileSpreadsheet className="h-4 w-4 mr-2" />{kbBusy === "gb-xlsx" ? "Bezig…" : "Grootboek (Excel)"}
          </Button>
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv"><Download className="h-4 w-4 mr-2" />{t.export}</Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="print-report"><Printer className="h-4 w-4 mr-2" />{t.print}</Button>
        </div>
      </div>

      <Card className="shadow-sm no-print">
        <CardContent className="p-4 flex flex-wrap gap-4 items-end">
          <div className="space-y-1"><Label>Van</Label><Input type="date" data-testid="report-start" value={start} onChange={(e) => setStart(e.target.value)} /></div>
          <div className="space-y-1"><Label>Tot</Label><Input type="date" data-testid="report-end" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
          <div className="space-y-1">
            <Label>Vestiging</Label>
            <Select value={locId} onValueChange={setLocId}>
              <SelectTrigger className="w-56" data-testid="report-location"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle vestigingen (centralisatie)</SelectItem>
                {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {saldo && (
        <Card className="shadow-sm" data-testid="btw-saldo-card">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-lg">BTW-saldo · {start} → {end}</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-lg border-2 border-red-200 bg-red-50/60 p-4" data-testid="saldo-verschuldigd">
                <div className="text-xs uppercase tracking-wide text-red-700/80">Verschuldigde btw (schuld)</div>
                <div className="text-2xl font-semibold text-red-700 tabular-nums mt-1">{eur(saldo.verschuldigd)}</div>
                <div className="text-[11px] text-red-700/70 mt-1">Dagboek {eur(saldo.verschuldigd_dagboek)} + diverse opbrengsten {eur(saldo.verschuldigd_opbrengsten)}</div>
              </div>
              <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/60 p-4" data-testid="saldo-aftrekbaar">
                <div className="text-xs uppercase tracking-wide text-emerald-700/80">Aftrekbare btw (vordering)</div>
                <div className="text-2xl font-semibold text-emerald-700 tabular-nums mt-1">{eur(saldo.aftrekbaar)}</div>
                <div className="text-[11px] text-emerald-700/70 mt-1">Terug te vorderen op aankopen &amp; kosten</div>
              </div>
              <div className={`rounded-lg border-2 p-4 ${saldo.saldo > 0 ? "border-red-300 bg-red-100/60" : "border-emerald-300 bg-emerald-100/60"}`} data-testid="saldo-netto">
                <div className={`text-xs uppercase tracking-wide ${saldo.saldo > 0 ? "text-red-700/80" : "text-emerald-700/80"}`}>{saldo.saldo > 0 ? "Te betalen aan de Staat" : saldo.saldo < 0 ? "Terug te krijgen van de Staat" : "Saldo"}</div>
                <div className={`text-3xl font-bold tabular-nums mt-1 ${saldo.saldo > 0 ? "text-red-700" : "text-emerald-700"}`} data-testid="saldo-netto-value">{eur(Math.abs(saldo.saldo))}</div>
                <div className={`text-[11px] mt-1 ${saldo.saldo > 0 ? "text-red-700/70" : "text-emerald-700/70"}`}>{saldo.saldo > 0 ? "Verschuldigd − aftrekbaar = schuld" : "Aftrekbaar − verschuldigd = vordering"}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {aangifte && (
        <Card className="shadow-sm" data-testid="btw-aangifte-card">
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="font-heading text-lg">BTW-aangifte · officiële roosters · {start} → {end}</CardTitle>
                <p className="text-sm text-muted-foreground">Klaar om over te nemen in de periodieke btw-aangifte (Intervat). Controleer vóór indiening.</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="outline" size="sm" disabled={kbBusy === "aangifte-pdf"} data-testid="download-aangifte-pdf" onClick={() => downloadAangifte("pdf")}>
                  <Download className="h-4 w-4 mr-2" />{kbBusy === "aangifte-pdf" ? "Bezig…" : "PDF"}
                </Button>
                <Button variant="outline" size="sm" disabled={kbBusy === "aangifte-xlsx"} data-testid="download-aangifte-xlsx" onClick={() => downloadAangifte("xlsx")}>
                  <Download className="h-4 w-4 mr-2" />{kbBusy === "aangifte-xlsx" ? "Bezig…" : "Excel"}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Kader II · Uitgaande handelingen (maatstaf)</div>
              <Table>
                <TableBody>
                  {[["00", "Bijzondere regeling / 0% (tabak, lotto, kranten)"], ["01", "Verkopen 6%"], ["02", "Verkopen 12%"], ["03", "Verkopen 21%"]].map(([code, lbl]) => (
                    <Fragment key={code}>
                    <TableRow data-testid={`rooster-${code}`}>
                      <TableCell className="w-14 font-mono font-semibold text-primary">{code}</TableCell>
                      <TableCell className="text-sm">{lbl}</TableCell>
                      <TableCell className="text-right tabular-nums">{eur(aangifte[code])}</TableCell>
                    </TableRow>
                    {code === "00" && (
                      <TableRow data-testid="rooster-00-lotto" className="text-muted-foreground">
                        <TableCell className="w-14"></TableCell>
                        <TableCell className="text-xs italic">↳ waarvan Lotto (0%) — zit in rooster 00</TableCell>
                        <TableCell className="text-right tabular-nums text-xs">{eur(aangifte["00_lotto"] || 0)}</TableCell>
                      </TableRow>
                    )}
                    </Fragment>
                  ))}
                  <TableRow className="bg-accent font-semibold" data-testid="rooster-54">
                    <TableCell className="font-mono">54</TableCell>
                    <TableCell className="text-sm">Verschuldigde btw (op 01·02·03)</TableCell>
                    <TableCell className="text-right tabular-nums text-red-700">{eur(aangifte["54"])}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Kader III/V · Inkomende handelingen &amp; aftrek</div>
              <Table>
                <TableBody>
                  {[["81", "Aankopen handelsgoederen (maatstaf)"], ["82", "Diensten & diverse goederen (maatstaf)"], ["83", "Bedrijfsmiddelen (maatstaf)"]].map(([code, lbl]) => (
                    <TableRow key={code} data-testid={`rooster-${code}`}>
                      <TableCell className="w-14 font-mono font-semibold text-primary">{code}</TableCell>
                      <TableCell className="text-sm">{lbl}</TableCell>
                      <TableCell className="text-right tabular-nums">{eur(aangifte[code])}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-accent font-semibold" data-testid="rooster-59">
                    <TableCell className="font-mono">59</TableCell>
                    <TableCell className="text-sm">Aftrekbare btw</TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-700">{eur(aangifte["59"])}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <div className="mt-4 rounded-lg border-2 p-3 flex items-center justify-between"
                style={{ borderColor: aangifte["71"] > 0 ? "rgb(252 165 165)" : "rgb(167 243 208)", background: aangifte["71"] > 0 ? "rgb(254 242 242)" : "rgb(236 253 245)" }}
                data-testid="rooster-saldo">
                <div>
                  <div className="font-mono font-semibold text-sm">{aangifte["71"] > 0 ? "71" : "72"}</div>
                  <div className="text-xs text-muted-foreground">{aangifte["71"] > 0 ? "Aan de Staat verschuldigd (te betalen)" : "Door de Staat verschuldigd (tegoed)"}</div>
                </div>
                <div className={`text-2xl font-bold tabular-nums ${aangifte["71"] > 0 ? "text-red-700" : "text-emerald-700"}`} data-testid="rooster-saldo-value">
                  {eur(aangifte["71"] > 0 ? aangifte["71"] : aangifte["72"])}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}


      {report && (
        <>
          <div className="hidden print:block mb-4">
            <h2 className="font-heading text-2xl">{t.title}</h2>
            <p className="text-sm">Singh Express Shop BV · BE 0788.952.072 · {start} → {end}</p>
          </div>
          <Card className="shadow-sm">
            <CardHeader><CardTitle className="font-heading text-lg">{t.title} · {start} → {end}</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t.rate}</TableHead>
                    <TableHead className="text-right">{t.gross}</TableHead>
                    <TableHead className="text-right">{t.base}</TableHead>
                    <TableHead className="text-right">{t.vat}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {RATE_CODES.map((r) => (
                    <TableRow key={r} data-testid={`report-rate-${r}`}>
                      <TableCell className="font-medium">{RATE_LABELS[r]}</TableCell>
                      <TableCell className="text-right tabular-nums">{eur(report.per_rate[r].gross)}</TableCell>
                      <TableCell className="text-right tabular-nums">{eur(report.per_rate[r].base)}</TableCell>
                      <TableCell className="text-right tabular-nums">{eur(report.per_rate[r].vat)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-accent font-semibold">
                    <TableCell>{t.total}</TableCell>
                    <TableCell className="text-right tabular-nums" data-testid="report-total-gross">{eur(report.total.gross)}</TableCell>
                    <TableCell className="text-right tabular-nums">{eur(report.total.base)}</TableCell>
                    <TableCell className="text-right tabular-nums" data-testid="report-total-vat">{eur(report.total.vat)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <p className="text-xs text-muted-foreground px-4 py-3">
                {report.entry_count} {t.entries} · {report.correction_count} correcties inbegrepen
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-sm no-print">
            <CardHeader><CardTitle className="font-heading text-lg">{t.base} / {t.vat}</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(214 32% 91%)" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} width={56} />
                  <Tooltip formatter={(v) => eur(v)} />
                  <Legend />
                  <Bar dataKey="base" name={t.base} fill="hsl(215 16% 47%)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="vat" name={t.vat} fill="hsl(221 83% 53%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {Object.keys(report.per_location).length > 1 && (
            <Card className="shadow-sm">
              <CardHeader><CardTitle className="font-heading text-lg">{t.central}</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow><TableHead>Vestiging</TableHead>{RATE_CODES.map((r) => <TableHead key={r} className="text-right">{RATE_LABELS[r]}</TableHead>)}<TableHead className="text-right">{t.vat}</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(report.per_location).map(([loc, data]) => (
                      <TableRow key={loc}>
                        <TableCell className="font-medium">{loc}</TableCell>
                        {RATE_CODES.map((r) => <TableCell key={r} className="text-right tabular-nums">{eur(data[r].gross)}</TableCell>)}
                        <TableCell className="text-right tabular-nums">{eur(RATE_CODES.reduce((s, r) => s + data[r].vat, 0))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
